/**
 * Theme Utils v1.0.0
 *
 * Minimal utilities that can't be handled by Alpine.
 * Loaded via defer in snippets/scripts.liquid.
 *
 * Contains:
 * - store.dialog API (for navbar mega menus, cart drawer, etc.)
 * - Quantity stepper delegation (data-qty="inc/dec")
 * - Cart add delegation (data-add-to-cart) for backwards compat
 * - Blog pagination
 * - Contact form scroll fix
 */
(function() {
  'use strict';

  const root = window;
  const store = (root.store = root.store || {});

  // ═══════════════════════════════════════════════════════════════════════════
  // DIALOG API
  // Used by navbar.js for mega menus, cart drawer, search, localization
  // ═══════════════════════════════════════════════════════════════════════════

  const getEl = (idOrSel) => {
    try {
      const id = String(idOrSel || '').replace(/^#/, '');
      return id ? document.getElementById(id) : null;
    } catch (_) { return null; }
  };

  store.dialog = store.dialog || {};

  store.dialog.open = (idOrSel) => {
    try {
      const el = getEl(idOrSel);
      if (!el) return;
      if (typeof el.open === 'function') el.open();
      else {
        el.classList.remove('hidden');
        el.setAttribute('data-open', '');
        document.body.classList.add('overflow-hidden');
      }
    } catch (_) {}
  };

  store.dialog.close = (idOrSel) => {
    try {
      const el = getEl(idOrSel);
      if (!el) return;
      if (typeof el.close === 'function') el.close();
      else {
        el.removeAttribute('data-open');
        if (el.__hadHiddenInitial) el.classList.add('hidden');
        document.body.classList.remove('overflow-hidden');
      }
    } catch (_) {}
  };

  store.dialog.toggle = (idOrSel) => {
    try {
      const el = getEl(idOrSel);
      if (!el) return;
      el.hasAttribute('data-open') ? store.dialog.close(idOrSel) : store.dialog.open(idOrSel);
    } catch (_) {}
  };

  // Track initial hidden state
  const trackHidden = () => {
    document.querySelectorAll('[data-modal-root], dialog, [role="dialog"]').forEach((el) => {
      if (el.classList.contains('hidden')) el.__hadHiddenInitial = true;
    });
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // QUANTITY STEPPER
  // Handles data-qty="inc/dec" buttons
  // ═══════════════════════════════════════════════════════════════════════════

  const findQtyInput = (btn) => {
    const scopes = [btn.closest('form'), btn.closest('[data-product-id]'), document].filter(Boolean);
    for (const s of scopes) {
      const inp = s.querySelector('input[name="quantity"]');
      if (inp) return inp;
    }
    return null;
  };

  document.addEventListener('click', (ev) => {
    const btn = ev.target?.closest?.('[data-qty]');
    if (!btn) return;

    const dir = btn.getAttribute('data-qty');
    if (dir !== 'inc' && dir !== 'dec') return;

    ev.preventDefault();
    ev.stopPropagation();

    const input = findQtyInput(btn);
    if (!input) return;

    const min = Math.max(1, parseInt(input.min || '1', 10) || 1);
    const max = input.max ? parseInt(input.max, 10) : null;
    const step = Math.max(1, parseInt(input.step || '1', 10) || 1);
    let val = Math.max(min, parseInt(input.value, 10) || min);

    val = dir === 'inc' ? val + step : val - step;
    if (val < min) val = min;
    if (max && val > max) val = max;

    input.value = val;
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }, true);

  // ═══════════════════════════════════════════════════════════════════════════
  // CART ADD DELEGATION
  // Handles data-add-to-cart for legacy/fallback forms
  // New code should use Alpine: @click="$cart.add({ id: variantId })"
  // ═══════════════════════════════════════════════════════════════════════════

  const findVariantId = (el) => {
    let id = el.getAttribute?.('data-variant-id');
    if (id) return id;

    const wrap = el.closest?.('[data-product-id]');
    if (wrap) {
      id = wrap.getAttribute?.('data-variant-id');
      if (id) return id;
    }

    const scopes = [el.closest('form'), wrap, el.closest('.shopify-section'), document].filter(Boolean);
    for (const s of scopes) {
      const inp = s.querySelector?.('input[name="id"], select[name="id"]');
      if (inp) return inp.value;
    }
    return null;
  };

  const findQty = (el) => {
    const attr = el.getAttribute?.('data-quantity');
    if (attr) return Math.max(1, parseInt(attr, 10) || 1);

    const wrap = el.closest?.('[data-product-id]') || el.closest('form');
    if (wrap) {
      const inp = wrap.querySelector?.('input[name="quantity"]');
      if (inp?.value) return Math.max(1, parseInt(inp.value, 10) || 1);
    }
    return 1;
  };

  document.addEventListener('click', async (ev) => {
    const el = ev.target?.closest?.('[data-add-to-cart]');
    if (!el) return;

    ev.preventDefault();
    ev.stopPropagation();

    const variantId = findVariantId(el);
    if (!variantId) return;

    const qty = findQty(el);

    // Single cart writer (issue #20): route through <cart-engine>, which
    // POSTs, morphs the drawer section, broadcasts store:cart:update, and
    // announces the add (store:cart:added { openDrawer: true }) — the drawer
    // opens itself.
    try {
      if (window.cartEngine) await window.cartEngine.add({ id: Number(variantId), quantity: qty });
    } catch (e) {
      console.warn('[cart-add]', e);
    }
  }, true);

  // ═══════════════════════════════════════════════════════════════════════════
  // BLOG PAGINATION
  // Handles data-blog-pagination prev/next
  // ═══════════════════════════════════════════════════════════════════════════

  document.addEventListener('click', (ev) => {
    const btn = ev.target?.closest?.('[data-blog-pagination] [data-action]');
    if (!btn) return;

    const wrap = btn.closest('[data-blog-pagination]');
    if (!wrap) return;

    ev.preventDefault();

    const action = btn.getAttribute('data-action');
    const param = (wrap.getAttribute('data-param') || 'page').toLowerCase();
    const pages = Math.max(1, parseInt(wrap.getAttribute('data-pages') || '1', 10));
    const current = Math.max(1, parseInt(wrap.getAttribute('data-page') || '1', 10));

    let next = action === 'prev' ? current - 1 : action === 'next' ? current + 1 : current;
    next = Math.max(1, Math.min(pages, next));
    if (next === current) return;

    // Update URL
    const url = new URL(location.href);
    next <= 1 ? url.searchParams.delete(param) : url.searchParams.set(param, next);

    try {
      history.pushState({}, '', url);
      const sid = wrap.closest('[id^="shopify-section-"]')?.id?.replace('shopify-section-', '');
      if (sid && store.refreshSections) store.refreshSections([sid]);
      else location.assign(url);
    } catch (_) {
      location.assign(url);
    }
  }, true);

  // ═══════════════════════════════════════════════════════════════════════════
  // CONTACT FORM SCROLL
  // Prevents hash-driven jump after form submission
  // ═══════════════════════════════════════════════════════════════════════════

  const handleContactScroll = () => {
    const form = document.querySelector('form[id^="contact-form-"]');
    if (!form) return;

    const hasFeedback = form.querySelector('[role="status"], [role="alert"]');
    const hashMatch = /contact[_-]?form/i.test(location.hash || '');

    if (!hasFeedback && !hashMatch) return;

    if (hashMatch) {
      try { history.replaceState(null, '', location.pathname + location.search); } catch (_) {}
    }

    requestAnimationFrame(() => {
      const hero = document.getElementById('contact-hero');
      hero ? hero.scrollIntoView({ block: 'start' }) : window.scrollTo({ top: 0 });
    });
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // INIT
  // ═══════════════════════════════════════════════════════════════════════════

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      trackHidden();
      handleContactScroll();
    }, { once: true });
  } else {
    trackHidden();
    handleContactScroll();
  }
})();
